import { ErrorBoundary, LoadingFallback } from "components/common";
import { RootLayout } from "layouts/RootLayout";
import NotificationList from "pages/Notification/index";
import StaffAddPage from "pages/Staff/StaffAddPage";
import StaffDetailsEdit from "pages/Staff/StaffDetailsEdit";
import { JSX, lazy, Suspense } from "react";
import {
  createBrowserRouter,
  matchPath,
  Route,
  RouteObject,
  Routes,
  useLocation,
} from "react-router-dom";
import useAuthContext from "stores/auth-context";
import { AppRouteType } from "types";

const CommandList = lazy(() => import("pages/Command/CommandList"));
const CreateCommand = lazy(() => import("pages/Command/CreateCommand"));
const ViewDetailCommand = lazy(() => import("pages/Command/ViewDetailCommand"));
const StaffList = lazy(() => import("pages/Staff/StaffList"));
const StaffDetail = lazy(() => import("pages/Staff/StaffDetails"));
const CustomerList = lazy(() => import("pages/Customers/CustomerList"));
const CustomerDetails = lazy(() => import("pages/Customers/CustomerDetails"));
const CustomerDetailsEdit = lazy(
  () => import("pages/Customers/CustomerDetailsEdit"),
);
const ContainerList = lazy(() => import("pages/Containers/ContainerList"));
const ContainerDetailPage = lazy(
  () => import("pages/Containers/ContainerDetailPage"),
);
const VehicleList = lazy(() => import("pages/Vehicles/VehicleList"));
const VehicleCreatePage = lazy(() => import("pages/Vehicles/VehicleCreatePage"));
const VehicleDetailPage = lazy(() => import("pages/Vehicles/VehicleDetailPage"));
const TrailerCreatePage = lazy(() => import("pages/Vehicles/TrailerCreatePage"));
const TrailerDetailPage = lazy(() => import("pages/Vehicles/TrailerDetailPage"));
const LoginPage = lazy(() => import("pages/Auth/Login/LoginPage"));
const NotFound = lazy(() => import("pages/NotFound/NotFound"));
const PayrollList = lazy(() => import("pages/Payroll/PayrollList"));
const PayrollDetail = lazy(() => import("pages/Payroll/PayrollDetail"));
const PayrollForm = lazy(() => import("pages/Payroll/PayrollForm"));
// const ExampleUseTableCommonPage = lazy(
//   () => import("pages/Example/ExampleUseTableCommon")
// );

export const routes: (AppRouteType | RouteObject)[] = [
  {
    name: "Login",
    path: "/login",
    element: <LoginPage />,
    auth: false,
    isPublic: true,
    noLayout: true,
    noWrapper: true,
    errorElement: <ErrorBoundary />,
  },
  // {
  //   path: "/",
  //   element: <ExampleUseTableCommonPage />,
  //   errorElement: <ErrorBoundary />,
  //   auth: true,
  //   isPublic: false,
  // },
  {
    path: "/command-list",
    element: <CommandList />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/command-list/create",
    element: <CreateCommand />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/command-list/detail/:id",
    element: <ViewDetailCommand />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/staff",
    element: <StaffList />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/staff/add/:type",
    element: <StaffAddPage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/staff/:id",
    element: <StaffDetail />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/staff/edit/:id",
    element: <StaffDetailsEdit />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/customers",
    element: <CustomerList />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/customers/edit/:id",
    element: <CustomerDetailsEdit />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/customers/:id",
    element: <CustomerDetails />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/vehicles",
    element: <VehicleList />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/containers",
    element: <ContainerList />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/containers/:id",
    element: <ContainerDetailPage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/vehicles/create",
    element: <VehicleCreatePage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    // NOTE: Must come AFTER /settings/vehicles/create to avoid :id matching "create"
    path: "/settings/vehicles/:id",
    element: <VehicleDetailPage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/trailers/create",
    element: <TrailerCreatePage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/settings/trailers/:id",
    element: <TrailerDetailPage />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/notifications",
    element: <NotificationList />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },

  {
    path: "/finance/payroll",
    element: <PayrollList />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/finance/payroll/:id",
    element: <PayrollDetail />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/finance/payroll/create",
    element: <PayrollForm />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
  {
    path: "/finance/payroll/edit/:id",
    element: <PayrollForm />,
    errorElement: <ErrorBoundary />,
    auth: true,
    isPublic: false,
  },
];

// Create the router instance
const router = createBrowserRouter(routes);

export const BasePage = router;

function waitFor(Component: undefined | JSX.Element) {
  if (!Component) {
    // Return a fallback component if the component is undefined
    return <NotFound />;
  }
  return <Suspense fallback={<LoadingFallback />}>{Component}</Suspense>;
}

export default function AppRoutes() {
  const { accessToken } = useAuthContext();
  const location = useLocation();

  const publicRoutes = (routes as AppRouteType[])
    .filter((route) => !route.auth || route.isPublic)
    .map((route) => (
      <Route
        key={route.path}
        path={route.path}
        element={waitFor(route.element)}
      />
    ));

  // public routes
  if (!accessToken) return <Routes>{publicRoutes}</Routes>;

  // 404 routes
  const isNotFoundPage = !routes.some(
    (route) =>
      route.path !== "*" &&
      route.path &&
      matchPath(route.path, location.pathname)
  );

  if (isNotFoundPage) {
    return (
      <Routes>
        <Route path="*" element={waitFor(<NotFound />)} />
      </Routes>
    );
  }

  const authenticatedRoutes = (routes as AppRouteType[])
    .filter(
      (route) =>
        ((route.auth && !route.permissions) || route.isPublic) &&
        route.path !== "*"
    )
    .map((route) => {
      const routeElement = (
        //   route.setting ? (
        //     <SettingLayout>{waitFor(route.element)}</SettingLayout>
        //   ) : route.noLayout && !route.noWrapper ? (
        //     <LayoutNoHeader>{waitFor(route.element)}</LayoutNoHeader>
        //   ) : route.noWrapper ? (
        //     <>{waitFor(route.element)}</>
        //   ) : (
        <RootLayout>{waitFor(route.element)}</RootLayout>
      );
      //   );
      return (
        <Route key={route.path} path={route.path} element={routeElement} />
      );
    });

  return <Routes>{authenticatedRoutes}</Routes>;
}
